import React, { useEffect, useState, useCallback, useContext, useRef, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { usePipelineContextSafe } from '../../../../pages/ClientPanel/Pipelines/PipelineContext'
import RenderField, { FieldInTicket } from '../../RenderField'
import { useForm, FormProvider } from 'react-hook-form'
import * as S from './style'
import { MoreHorizontal, Calendar, Paperclip, Plus, Copy, Link2, Archive, Trash2, Pencil, ExternalLink, Download, FileText, MoreVertical, ThumbsUp, ThumbsDown, CheckCircle, ArrowLeft } from 'lucide-react'
import CommentsOfOp from '../../../Modals/ViewPCP/Comments'
import ConfirmationModal from '../../../ModalConfirmation'
import { coverUrlManager } from '../../../../utils/coverUrlManager'
import StepTimeline from './StepTimeline'

type PdfJsModule = typeof import('pdfjs-dist')

const formatSubTicketTooltipDate = (isoString?: string | null) => {
  if (!isoString) return ''
  const d = new Date(isoString)
  const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${dateStr} às ${timeStr}`
}

const formatSubTicketDuration = (seconds: number) => {
  const absoluteSeconds = Math.max(0, Math.floor(seconds))
  const h = Math.floor(absoluteSeconds / 3600)
  const m = Math.floor((absoluteSeconds % 3600) / 60)
  const s = absoluteSeconds % 60
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${pad(h)}h${pad(m)}min${pad(s)}seg`
}

const formatReceivedFieldValue = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) {
      return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }
  }
  return value
}

interface TicketTask {
  id: number
  title: string
  completed: boolean
  ticketId: number
  taskListId: number | null
  taskListName: string | null
  createdAt: string
}

interface TicketTaskList {
  id: number
  name: string
  ticketId: number
  createdAt: string
  tasks: TicketTask[]
}

interface Attachment {
  id: number
  originalName: string
  fileName: string
  filePath: string
  fileSize: number
  mimeType: string
}

interface TagInTicket {
  tagId: number
  ticketId: number
  tag: {
    id: number
    name: string
    color: string
  }
}

interface Step {
  id: number
  name: string
  pipelineId: number
  position?: number
  allowCardCreation?: boolean
}

export interface TicketFile {
  generalFile: {
    id: number;
    fileUrl: string;
    mimeType: string;
    title: string;
  };
}

interface Ticket {
  id: number
  title: string
  status?: 'NEUTRO' | 'GANHOU' | 'PERDEU'
  deadline: Date
  urgency: boolean
  description: string
  stepId: number
  createdAt: string
  fieldInTickets?: FieldInTicket[]
  tags?: TagInTicket[]
  tasks?: TicketTask[]
  taskLists?: TicketTaskList[]
  attachments: Attachment[]
  fileInTickets?: TicketFile[];
  coverFile?: {
    id: number;
    fileUrl: string;
    title: string;
    originalName: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
  };
  step?: {
    id: number
    name: string
    pipelineId: number
    pipeline?: {
      id: number
      moduleType?: string | null
      subModule?: string | null
    } | null
  }
  saleStatus?: string | null
  externalId?: number | null
  externalData?: Record<string, any> | null
  parentTicketId?: number | null
  ParentTicket?: { id: number; title: string } | null
  TicketDispatchAsChild?: { id: number; status: string } | null
}

interface Tag {
  id: number
  name: string
  color: string
}

const actionButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '12px',
  color: '#475569',
  textAlign: 'left',
  transition: 'background-color 0.2s'
};

interface ViewOrEditTicketProps {
  previewTicketId?: number
  onClosePreview?: () => void
}

const ViewOrEditTicket: React.FC<ViewOrEditTicketProps> = ({ previewTicketId, onClosePreview }) => {
  const isPreview = previewTicketId !== undefined && previewTicketId !== null
  const [searchParams] = useSearchParams()
  const ticketId = isPreview ? String(previewTicketId) : searchParams.get('ticketId')
  const pipelineIdFromQuery = searchParams.get('pipelineId')
  const isFilled = searchParams.get('isFilled')
  const isFilledBoolean = isFilled === 'false' ? false : true
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [tagDropdownCoords, setTagDropdownCoords] = useState<{ top: number, left: number, width: number } | null>(null)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [optionsMenuPosition, setOptionsMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
  const optionsButtonRef = useRef<HTMLButtonElement | null>(null)
  const optionsMenuRef = useRef<HTMLDivElement | null>(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [newTaskListName, setNewTaskListName] = useState('')
  const [showAddTaskList, setShowAddTaskList] = useState(false)
  const [editingTaskListId, setEditingTaskListId] = useState<number | null>(null)
  const [editingTaskListName, setEditingTaskListName] = useState('')
  const [activeTaskListForNewTask, setActiveTaskListForNewTask] = useState<number | null>(null)
  const [newTaskTitleByListId, setNewTaskTitleByListId] = useState<Record<number, string>>({})
  const [attachmentMenuId, setAttachmentMenuId] = useState<number | null>(null)
  const [attachmentMenuPosition, setAttachmentMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
  const attachmentMenuRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const attachmentDropdownRef = useRef<HTMLDivElement | null>(null)
  const [taskMenuId, setTaskMenuId] = useState<number | null>(null)
  const [taskMenuPosition, setTaskMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
  const taskMenuRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const taskDropdownRef = useRef<HTMLDivElement | null>(null)
  const newTaskInputRef = useRef<HTMLInputElement | null>(null)
  const tagDropdownRef = useRef<HTMLDivElement | null>(null)
  const tagContainerRef = useRef<HTMLDivElement | null>(null)
  const [coverImage, setCoverImage] = useState<string | undefined>();
  const [activeFieldIdsByCurrentStep, setActiveFieldIdsByCurrentStep] = useState<Set<number>>(new Set())
  const [isGeneratingPdfCover, setIsGeneratingPdfCover] = useState(false)
  const progressiveAutosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastPersistedWriteIntegrationSnapshotRef = useRef<string>('')
  const isPersistingWriteIntegrationRef = useRef(false)
  const ticketRef = useRef<Ticket | null>(null)
  const [writeRegistrationCreateOnSaveToken, setWriteRegistrationCreateOnSaveToken] = useState(0)
  const writeRegistrationAutoCreateActivityByTokenRef = useRef<Map<number, number>>(new Map())

  const [showSendToPipelineModal, setShowSendToPipelineModal] = useState(false)
  const [availablePipelines, setAvailablePipelines] = useState<{id: number, name: string}[]>([])
  const [selectedTargetPipelineId, setSelectedTargetPipelineId] = useState<number | null>(null)
  const [availableTargetSteps, setAvailableTargetSteps] = useState<{id: number, name: string}[]>([])
  const [selectedTargetStepId, setSelectedTargetStepId] = useState<number | null>(null)
  const [sendingToPipeline, setSendingToPipeline] = useState(false)
  const [showFinishSubcardModal, setShowFinishSubcardModal] = useState(false)
  const [finishingSubcard, setFinishingSubcard] = useState(false)
  const [subTicketStepTooltip, setSubTicketStepTooltip] = useState<{ step: any; top: number; left: number; arrowLeft: number; below: boolean } | null>(null)
  const subTicketTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [previewSubTicketId, setPreviewSubTicketId] = useState<number | null>(null)
  const { setFeedback } = useContext(FeedbackContext)
  const [subTicketsTimeline, setSubTicketsTimeline] = useState<any[]>([])

  const methods = useForm()
  const { control } = methods

  const navigate = useNavigate()
  const pipelineContext = usePipelineContextSafe()
  const moduleType = pipelineContext?.moduleType

  const resolvePipelineId = useCallback(() => {
    if (pipelineIdFromQuery && pipelineIdFromQuery !== 'null') {
      return pipelineIdFromQuery
    }
    const pipelineIdFromTicket = ticket?.step?.pipelineId
    if (Number.isFinite(Number(pipelineIdFromTicket))) {
      return String(pipelineIdFromTicket)
    }
    return null
  }, [pipelineIdFromQuery, ticket?.step?.pipelineId])

  const buildTicketRoute = useCallback((targetTicketId: number | string) => {
    const queryParams = new URLSearchParams({ ticketId: String(targetTicketId) })
    const resolvedPipelineId = resolvePipelineId()
    if (resolvedPipelineId) {
      queryParams.set('pipelineId', resolvedPipelineId)
    }
    const returnTo = searchParams.get('returnTo')
    if (returnTo) {
      queryParams.set('returnTo', returnTo)
    }
    return `/panel/pipeline/ticket?${queryParams.toString()}`
  }, [resolvePipelineId, searchParams])

  const closeTicketModal = useCallback(() => {
    const returnTo = searchParams.get('returnTo')
    if (returnTo) {
      navigate(returnTo)
      return
    }
    const resolvedPipelineId = resolvePipelineId()
    if (resolvedPipelineId) {
      navigate(`/panel/pipeline?pipelineId=${resolvedPipelineId}`)
      return
    }
    navigate('/panel/pipelines')
  }, [navigate, resolvePipelineId, searchParams])

  const getActivePipelineFieldIdsByStep = useCallback(async (stepId: number): Promise<Set<number>> => {
    const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields`, {
      params: { stepId }
    })
    const activeFieldIds = (response.data?.data || [])
      .map((item: any) => item.pipelineFieldId ?? item.pipelineField?.id ?? item.PipelineField?.id)
      .filter((id: any) => Number.isFinite(Number(id)))
      .map((id: any) => Number(id))
    return new Set(activeFieldIds)
  }, [])

  const hasMeaningfulFieldValue = useCallback((value: any): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== ''
    if (typeof value === 'number') return Number.isFinite(value)
    if (typeof value === 'boolean') return true
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length > 0
    return false
  }, [])

  const getFieldValuePresence = useCallback((field: FieldInTicket) => {
    const hasStringValue = typeof field.stringValue === 'string' && field.stringValue.trim() !== ''

    let hasBigStringValue = false
    if (typeof field.bigStringValue === 'string' && field.bigStringValue.trim() !== '') {
      const rawBigStringValue = field.bigStringValue.trim()
      try {
        const parsedBigStringValue = JSON.parse(rawBigStringValue)
        const isVersionedDraftStore =
          parsedBigStringValue &&
          typeof parsedBigStringValue === 'object' &&
          !Array.isArray(parsedBigStringValue) &&
          Number(parsedBigStringValue.version) === 2 &&
          Array.isArray(parsedBigStringValue.drafts)

        if (isVersionedDraftStore) {
          hasBigStringValue = parsedBigStringValue.drafts.some((draftItem: any) => {
            const draftData = draftItem?.data
            if (!draftData || typeof draftData !== 'object' || Array.isArray(draftData)) return false
            return Object.values(draftData).some((draftValue) => hasMeaningfulFieldValue(draftValue))
          })
        } else {
          hasBigStringValue = true
        }
      } catch {
        hasBigStringValue = true
      }
    }

    const hasIntegerValue = field.integerValue !== null && field.integerValue !== undefined
    const hasDecimalValue = field.decimalValue !== null && field.decimalValue !== undefined
    const hasDateTimeValue = field.dateTimeValue !== null && field.dateTimeValue !== undefined && String(field.dateTimeValue).trim() !== ''
    const hasBooleanValue = field.booleanValue !== null && field.booleanValue !== undefined

    const integrationDataValue = (field as any).integrationData
    const integrationDataSelectionsValue = (field as any).integrationDataSelections
    const integrationDataIdValue = (field as any).integrationDataId

    const hasIntegrationData = Boolean(
      integrationDataValue &&
      (
        hasMeaningfulFieldValue(integrationDataValue?.id) ||
        hasMeaningfulFieldValue(integrationDataValue?.name)
      )
    )

    const hasIntegrationDataId = integrationDataIdValue !== null && integrationDataIdValue !== undefined && String(integrationDataIdValue).trim() !== ''

    const hasIntegrationDataSelections = Array.isArray(integrationDataSelectionsValue) && integrationDataSelectionsValue.some((selection: any) => {
      if (!selection || typeof selection !== 'object') return false
      return hasMeaningfulFieldValue(selection.id) || hasMeaningfulFieldValue(selection.name)
    })

    const hasAnyValue =
      hasStringValue ||
      hasBigStringValue ||
      hasIntegerValue ||
      hasDecimalValue ||
      hasDateTimeValue ||
      hasBooleanValue ||
      hasIntegrationData ||
      hasIntegrationDataId ||
      hasIntegrationDataSelections

    return {
      hasStringValue,
      hasBigStringValue,
      hasIntegerValue,
      hasDecimalValue,
      hasDateTimeValue,
      hasBooleanValue,
      hasIntegrationData,
      hasIntegrationDataId,
      hasIntegrationDataSelections,
      hasAnyValue,
    }
  }, [hasMeaningfulFieldValue])

  const hasFieldValue = useCallback((field: FieldInTicket) => {
    return getFieldValuePresence(field).hasAnyValue
  }, [getFieldValuePresence])

  const handleWriteRegistrationAutoCreateActivity = useCallback((payload: {
    phase: 'start' | 'finish'
    token: number
    executionKey: string
  }) => {
    const token = Number(payload?.token)
    if (!Number.isFinite(token) || token <= 0) return

    const currentCount = writeRegistrationAutoCreateActivityByTokenRef.current.get(token) || 0
    const nextCount = payload.phase === 'start'
      ? currentCount + 1
      : Math.max(0, currentCount - 1)

    writeRegistrationAutoCreateActivityByTokenRef.current.set(token, nextCount)

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] save-trigger-auto-create-activity', {
      token,
      phase: payload.phase,
      executionKey: payload.executionKey,
      currentCount,
      nextCount,
    })
  }, [])

  const waitForWriteRegistrationAutoCreateByToken = useCallback(async (token: number) => {
    const startedAt = Date.now()
    let hasObservedStart = false

    while (Date.now() - startedAt < 15000) {
      const currentCount = writeRegistrationAutoCreateActivityByTokenRef.current.get(token) || 0

      if (currentCount > 0) {
        hasObservedStart = true
      }

      if (hasObservedStart && currentCount === 0) {
        break
      }

      if (!hasObservedStart && Date.now() - startedAt > 1000) {
        break
      }

      await new Promise((resolve) => setTimeout(resolve, 40))
    }

    writeRegistrationAutoCreateActivityByTokenRef.current.delete(token)
  }, [])

  const summarizeProgressiveDraftStore = useCallback((bigStringValue?: string | null) => {
    const summary = {
      hasBigStringValue: typeof bigStringValue === 'string' && bigStringValue.trim() !== '',
      rawLength: typeof bigStringValue === 'string' ? bigStringValue.length : 0,
      parsedAsVersion2: false,
      activeDraftId: null as string | null,
      draftCount: 0,
      nonEmptyDraftCount: 0,
      nonEmptyDraftIds: [] as string[],
      parseError: '' as string,
    }

    if (!summary.hasBigStringValue) return summary

    try {
      const parsed = JSON.parse(String(bigStringValue))
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Number((parsed as any).version) === 2 && Array.isArray((parsed as any).drafts)) {
        summary.parsedAsVersion2 = true
        summary.activeDraftId = typeof (parsed as any).activeDraftId === 'string' ? (parsed as any).activeDraftId : null
        summary.draftCount = (parsed as any).drafts.length

        ;(parsed as any).drafts.forEach((draft: any) => {
          const draftId = typeof draft?.id === 'string' ? draft.id : 'unknown'
          const draftData = draft?.data
          if (!draftData || typeof draftData !== 'object' || Array.isArray(draftData)) return

          const hasMeaningfulData = Object.values(draftData).some((draftValue) => {
            if (draftValue === null || draftValue === undefined) return false
            if (typeof draftValue === 'string') return draftValue.trim() !== ''
            if (typeof draftValue === 'number') return Number.isFinite(draftValue)
            if (typeof draftValue === 'boolean') return true
            if (Array.isArray(draftValue)) return draftValue.length > 0
            if (typeof draftValue === 'object') return Object.keys(draftValue).length > 0
            return false
          })

          if (hasMeaningfulData) {
            summary.nonEmptyDraftCount += 1
            summary.nonEmptyDraftIds.push(draftId)
          }
        })
      }
    } catch (error: any) {
      summary.parseError = String(error?.message || 'parse_error')
    }

    return summary
  }, [])

  const summarizeFieldInTicketsForDebug = useCallback((fields: FieldInTicket[] | undefined | null) => {
    const sourceFields = Array.isArray(fields) ? fields : []

    return sourceFields.map((field) => {
      const progressiveSummary = summarizeProgressiveDraftStore(field.bigStringValue)

      return {
        pipelineFieldId: Number(field.pipelineFieldId),
        fieldName: field.pipelineField?.name,
        stepRequired: field.isRequired,
        hasStringValue: typeof field.stringValue === 'string' && field.stringValue.trim() !== '',
        hasIntegerValue: field.integerValue !== null && field.integerValue !== undefined,
        hasDecimalValue: field.decimalValue !== null && field.decimalValue !== undefined,
        hasDateTimeValue: field.dateTimeValue !== null && field.dateTimeValue !== undefined && String(field.dateTimeValue).trim() !== '',
        hasBooleanValue: field.booleanValue !== null && field.booleanValue !== undefined,
        integrationType: field.pipelineField?.integrationConfig?.integrationType,
        writeRegistrationType: field.pipelineField?.integrationConfig?.writeRegistrationType,
        progressiveDraft: progressiveSummary,
      }
    })
  }, [summarizeProgressiveDraftStore])

  const currentStepFields = useMemo(() => {
    if (!ticket?.fieldInTickets?.length) return []
    return ticket.fieldInTickets.filter((field) => activeFieldIdsByCurrentStep.has(Number(field.pipelineFieldId)))
  }, [ticket?.fieldInTickets, activeFieldIdsByCurrentStep])

  const currentStepWriteRegistrationFields = useMemo(() => {
    return currentStepFields.filter((field) => field.pipelineField?.integrationConfig?.integrationType === 'write')
  }, [currentStepFields])

  const getWriteRegistrationGroupKey = useCallback((field: FieldInTicket): string | null => {
    const integrationConfig = field.pipelineField?.integrationConfig
    if (!integrationConfig || integrationConfig.integrationType !== 'write') return null

    const writeRegistrationType = String(integrationConfig.writeRegistrationType || '').trim().toLowerCase()
    if (writeRegistrationType) return `write-type:${writeRegistrationType}`

    const moduleId = String(integrationConfig.moduleId || '').trim().toLowerCase()
    if (moduleId) return `module-id:${moduleId}`

    return `pipeline-field:${Number(field.pipelineFieldId)}`
  }, [])

  const progressiveWriteRegistrationFieldsForDisplay = useMemo(() => {
    const result: FieldInTicket[] = []
    const selectedGroupKeys = new Set<string>()

    const appendFieldByGroup = (field: FieldInTicket) => {
      const groupKey = getWriteRegistrationGroupKey(field)
      if (!groupKey) return
      if (selectedGroupKeys.has(groupKey)) return

      selectedGroupKeys.add(groupKey)
      result.push(field)
    }

    currentStepWriteRegistrationFields.forEach((field) => {
      appendFieldByGroup(field)
    })

    ;(ticket?.fieldInTickets || []).forEach((field) => {
      const isFromCurrentStep = activeFieldIdsByCurrentStep.has(Number(field.pipelineFieldId))
      if (isFromCurrentStep) return
      if (!hasFieldValue(field)) return
      appendFieldByGroup(field)
    })

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] display-write-groups', {
      ticketId: ticket?.id,
      stepId: ticket?.stepId,
      currentStepWriteFieldIds: currentStepWriteRegistrationFields.map((field) => Number(field.pipelineFieldId)),
      progressiveWriteDisplayFieldIds: result.map((field) => Number(field.pipelineFieldId)),
      progressiveWriteDisplayGroupKeys: result.map((field) => getWriteRegistrationGroupKey(field)),
    })

    return result
  }, [activeFieldIdsByCurrentStep, currentStepWriteRegistrationFields, getWriteRegistrationGroupKey, hasFieldValue, ticket?.fieldInTickets, ticket?.id, ticket?.stepId])

  const currentStepRegularFields = useMemo(() => {
    return currentStepFields.filter((field) => field.pipelineField?.integrationConfig?.integrationType !== 'write')
  }, [currentStepFields])

  const filledFieldsFromOtherSteps = useMemo(() => {
    if (!ticket?.fieldInTickets?.length) return []
    const evaluatedFields = ticket.fieldInTickets.map((field) => {
      const isFromCurrentStep = activeFieldIdsByCurrentStep.has(Number(field.pipelineFieldId))
      const valuePresence = getFieldValuePresence(field)
      return {
        field,
        isFromCurrentStep,
        valuePresence,
      }
    })

    const result = evaluatedFields
      .filter((entry) => {
        if (entry.isFromCurrentStep) return false
        if (!entry.valuePresence.hasAnyValue) return false

        const integrationType = String(entry.field.pipelineField?.integrationConfig?.integrationType || '').trim().toLowerCase()
        if (integrationType === 'write') return false

        return true
      })
      .map((entry) => entry.field)

    console.debug('[PIPELINE-FIELDS-GLOBAL] filled-fields-evaluation', {
      ticketId: ticket?.id,
      stepId: ticket?.stepId,
      totalFieldCount: evaluatedFields.length,
      currentStepFieldIds: Array.from(activeFieldIdsByCurrentStep),
      progressiveWriteDisplayFieldIds: progressiveWriteRegistrationFieldsForDisplay.map((field) => Number(field.pipelineFieldId)),
      filledFieldsFromOtherStepsIds: result.map((field) => Number(field.pipelineFieldId)),
      evaluatedFields: evaluatedFields.map((entry) => ({
        pipelineFieldId: Number(entry.field.pipelineFieldId),
        fieldName: entry.field.pipelineField?.name,
        integrationType: entry.field.pipelineField?.integrationConfig?.integrationType,
        writeRegistrationType: entry.field.pipelineField?.integrationConfig?.writeRegistrationType,
        isFromCurrentStep: entry.isFromCurrentStep,
        ...entry.valuePresence,
      })),
    })

    return result
  }, [ticket?.fieldInTickets, ticket?.id, ticket?.stepId, activeFieldIdsByCurrentStep, getFieldValuePresence, progressiveWriteRegistrationFieldsForDisplay])

  const taskListsForDisplay = useMemo(() => {
    if (!ticket) return [] as { id: number | null; name: string; tasks: TicketTask[] }[]

    const ticketTasks = Array.isArray(ticket.tasks) ? ticket.tasks : []
    const explicitTaskLists = Array.isArray(ticket.taskLists) ? ticket.taskLists : []
    const tasksByListId = new Map<number, TicketTask[]>()

    for (const task of ticketTasks) {
      if (typeof task.taskListId === 'number') {
        const existingTasks = tasksByListId.get(task.taskListId) || []
        existingTasks.push(task)
        tasksByListId.set(task.taskListId, existingTasks)
      }
    }

    const normalizedLists: Array<{ id: number | null; name: string; tasks: TicketTask[] }> = explicitTaskLists.map((taskList) => {
      const nestedTasks = Array.isArray(taskList.tasks) ? taskList.tasks : []
      const fallbackTasks = tasksByListId.get(taskList.id) || []
      return {
        id: taskList.id,
        name: taskList.name,
        tasks: nestedTasks.length > 0 ? nestedTasks : fallbackTasks,
      }
    })

    const knownListIds = new Set(normalizedLists.map((taskList) => taskList.id))
    const tasksWithoutList = ticketTasks.filter((task) => {
      if (task.taskListId === null || task.taskListId === undefined) {
        return true
      }
      return !knownListIds.has(task.taskListId)
    })

    if (tasksWithoutList.length > 0) {
      normalizedLists.push({
        id: null,
        name: 'Sem lista',
        tasks: tasksWithoutList,
      })
    }

    return normalizedLists
  }, [ticket])


  const normalizeTagInTicket = (tagTicket: any) => {
    const tagItem = tagTicket?.tag || tagTicket?.Tag || {}
    const idFromTag = tagItem?.id ?? tagTicket?.tagId

    return {
      tagId: tagTicket?.tagId ?? idFromTag,
      ticketId: tagTicket?.ticketId ?? null,
      tag: {
        id: idFromTag,
        name: tagItem?.name || tagTicket?.name || 'Tag',
        color: tagItem?.color || '#999'
      }
    }
  }

  const normalizeFullTicket = (data: any) => {
    if (!data) return data
    const mapped: any = { ...data }

    const normalizeTask = (task: any): TicketTask => ({
      id: task.id,
      title: task.title,
      completed: task.completed,
      ticketId: task.ticketId,
      taskListId: task.taskListId ?? task.taskList?.id ?? task.TicketTaskList?.id ?? null,
      taskListName: task.taskListName ?? task.taskList?.name ?? task.TicketTaskList?.name ?? null,
      createdAt: task.createdAt,
    })

    const normalizeTaskList = (taskList: any): TicketTaskList => ({
      id: taskList.id,
      name: taskList.name,
      ticketId: taskList.ticketId,
      createdAt: taskList.createdAt,
      tasks: (taskList.TicketTask || taskList.tasks || []).map((task: any) => normalizeTask(task)),
    })

    if (data.TagTicket && Array.isArray(data.TagTicket)) {
      mapped.tags = data.TagTicket.map((tagTicket: any) => normalizeTagInTicket(tagTicket))
    } else if (data.tags && Array.isArray(data.tags)) {
      mapped.tags = data.tags.map((tagTicket: any) => normalizeTagInTicket(tagTicket))
    }

    if (data.TicketTask && Array.isArray(data.TicketTask)) {
      mapped.tasks = data.TicketTask.map((task: any) => normalizeTask(task))
    } else if (data.tasks && Array.isArray(data.tasks)) {
      mapped.tasks = data.tasks.map((task: any) => normalizeTask(task))
    }

    if (data.TicketTaskList && Array.isArray(data.TicketTaskList)) {
      mapped.taskLists = data.TicketTaskList.map((taskList: any) => normalizeTaskList(taskList))
    } else if (data.taskLists && Array.isArray(data.taskLists)) {
      mapped.taskLists = data.taskLists.map((taskList: any) => normalizeTaskList(taskList))
    }

    if (data.GeneralFile) {
      mapped.coverFile = {
        id: data.GeneralFile.id,
        fileUrl: data.GeneralFile.fileUrl,
        title: data.GeneralFile.title,
        originalName: data.GeneralFile.originalName,
        fileName: data.GeneralFile.fileName,
        fileSize: data.GeneralFile.fileSize,
        mimeType: data.GeneralFile.mimeType
      }
    }
    return mapped
  }

  const refreshTicketDetails = useCallback(async () => {
    const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
    if (response.data?.data) {
      setTicket(normalizeFullTicket(response.data.data))
    }
  }, [ticketId])

  useEffect(() => {
    const updateCoverUrl = async () => {
      if (ticket?.coverFile?.fileUrl && ticket.coverFile.id) {
        try {
          const url = await coverUrlManager.getCoverUrl(ticket.coverFile.id, ticket.coverFile.fileUrl);
          setCoverImage(url);
        } catch (error) {
          console.error('Erro ao obter URL da capa:', error);
          setCoverImage(ticket.coverFile.fileUrl);
        }
      } else {
        setCoverImage(undefined);
      }
    };

    updateCoverUrl();
  }, [ticket?.coverFile]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (attachmentDropdownRef.current && !attachmentDropdownRef.current.contains(e.target as Node)) {
        const isMenuButton = Object.values(attachmentMenuRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isMenuButton) {
          setAttachmentMenuId(null)
        }
      }
      if (taskDropdownRef.current && !taskDropdownRef.current.contains(e.target as Node)) {
        const isTaskMenuButton = Object.values(taskMenuRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isTaskMenuButton) {
          setTaskMenuId(null)
        }
      }
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        const isTagContainer = tagContainerRef.current && tagContainerRef.current.contains(e.target as Node)
        if (!isTagContainer) {
          setShowTagDropdown(false)
        }
      }
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target as Node)) {
        const isOptionsButton = optionsButtonRef.current && optionsButtonRef.current.contains(e.target as Node)
        if (!isOptionsButton) {
          setShowOptionsMenu(false)
        }
      }
    }
    const handleScrollOrResize = () => {
      setAttachmentMenuId(null)
      setTaskMenuId(null)
      setShowOptionsMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const handleToggleAttachmentMenu = (attachmentId: number) => {
    if (attachmentMenuId === attachmentId) {
      setAttachmentMenuId(null)
      return
    }
    const button = attachmentMenuRefs.current[attachmentId]
    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 120
      const menuHeight = 120
      let left = rect.left
      let top = rect.bottom + 5
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5
      }
      setAttachmentMenuPosition({ top, left })
    }
    setAttachmentMenuId(attachmentId)
  }

  const handleToggleTaskMenu = (taskId: number) => {
    if (taskMenuId === taskId) {
      setTaskMenuId(null)
      return
    }
    const button = taskMenuRefs.current[taskId]
    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 100
      const menuHeight = 50
      let left = rect.right - menuWidth
      let top = rect.bottom + 5
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5
      }
      setTaskMenuPosition({ top, left })
    }
    setTaskMenuId(taskId)
  }

  useEffect(() => {
    ticketRef.current = ticket
  }, [ticket])

  const serializeWriteIntegrationSnapshot = useCallback((fields: FieldInTicket[]) => {
    return JSON.stringify(
      fields
        .filter((field) => field.pipelineField?.integrationConfig?.integrationType === 'write')
        .map((field) => ({
          pipelineFieldId: Number(field.pipelineFieldId),
          integerValue: field.integerValue ?? null,
          decimalValue: field.decimalValue ?? null,
          stringValue: field.stringValue ?? null,
          bigStringValue: field.bigStringValue ?? null,
          dateTimeValue: field.dateTimeValue ?? null,
          booleanValue: field.booleanValue ?? null,
        }))
        .sort((leftField, rightField) => leftField.pipelineFieldId - rightField.pipelineFieldId)
    )
  }, [])

  useEffect(() => {
    if (!ticketId || !ticket?.fieldInTickets) return

    const writeIntegrationSnapshot = serializeWriteIntegrationSnapshot(ticket.fieldInTickets)
    console.debug('[PIPELINE-WRITE-STAGED][FRONT] autosave-check', {
      ticketId,
      currentSnapshotLength: writeIntegrationSnapshot.length,
      lastSnapshotLength: lastPersistedWriteIntegrationSnapshotRef.current.length,
      totalFields: ticket.fieldInTickets.length,
      writeFields: ticket.fieldInTickets.filter((field) => field.pipelineField?.integrationConfig?.integrationType === 'write').length,
      fieldsSummary: summarizeFieldInTicketsForDebug(ticket.fieldInTickets),
    })
    if (writeIntegrationSnapshot === lastPersistedWriteIntegrationSnapshotRef.current) return

    if (progressiveAutosaveTimeoutRef.current) {
      clearTimeout(progressiveAutosaveTimeoutRef.current)
    }

    progressiveAutosaveTimeoutRef.current = setTimeout(async () => {
      if (isPersistingWriteIntegrationRef.current) return

      const currentTicket = ticketRef.current
      if (!currentTicket?.fieldInTickets) return

      const currentSnapshot = serializeWriteIntegrationSnapshot(currentTicket.fieldInTickets)
      if (currentSnapshot === lastPersistedWriteIntegrationSnapshotRef.current) return

      console.debug('[PIPELINE-WRITE-STAGED][FRONT] autosave-patch-request', {
        ticketId,
        snapshotLength: currentSnapshot.length,
        fieldCount: currentTicket.fieldInTickets.length,
        fieldsSummary: summarizeFieldInTicketsForDebug(currentTicket.fieldInTickets),
      })

      isPersistingWriteIntegrationRef.current = true
      try {
        await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
          fieldInTickets: currentTicket.fieldInTickets,
        })
        lastPersistedWriteIntegrationSnapshotRef.current = currentSnapshot
        console.debug('[PIPELINE-WRITE-STAGED][FRONT] autosave-patch-success', {
          ticketId,
          snapshotLength: currentSnapshot.length,
        })
      } catch (error) {
        console.error('[PIPELINE-WRITE-STAGED] erro ao persistir rascunho progressivo:', error)
      } finally {
        isPersistingWriteIntegrationRef.current = false
      }
    }, 700)

    return () => {
      if (progressiveAutosaveTimeoutRef.current) {
        clearTimeout(progressiveAutosaveTimeoutRef.current)
      }
    }
  }, [serializeWriteIntegrationSnapshot, summarizeFieldInTicketsForDebug, ticket?.fieldInTickets, ticketId])

  useEffect(() => {
    return () => {
      if (progressiveAutosaveTimeoutRef.current) {
        clearTimeout(progressiveAutosaveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
        if (response.data) {
          const normalized = normalizeFullTicket(response.data.data)
          const allFields: FieldInTicket[] = Array.isArray(normalized.fieldInTickets) ? normalized.fieldInTickets : []
          const stepIdFromTicket = Number(normalized?.stepId || response.data.data?.stepId)
          let activeFieldIds = new Set<number>()

          if (Number.isFinite(stepIdFromTicket)) {
            try {
              activeFieldIds = await getActivePipelineFieldIdsByStep(stepIdFromTicket)
            } catch (stepFieldsError) {
              console.error('[ViewOrEditTicket] fetchStepFields error:', stepFieldsError)
            }
          }

          const currentStepFieldIds = allFields
            .filter((field) => activeFieldIds.has(Number(field.pipelineFieldId)))
            .map((field) => Number(field.pipelineFieldId))

          const otherStepFilledFieldIds = allFields
            .filter((field) => !activeFieldIds.has(Number(field.pipelineFieldId)))
            .filter((field) => hasFieldValue(field))
            .map((field) => Number(field.pipelineFieldId))

          setActiveFieldIdsByCurrentStep(activeFieldIds)
          console.debug('[ViewOrEditTicket] fetchTicket normalized:', normalized)
          console.debug('[PIPELINE-FIELDS-GLOBAL] fetchTicket field split:', {
            ticketId,
            stepId: stepIdFromTicket,
            totalFieldCount: allFields.length,
            currentStepFieldCount: currentStepFieldIds.length,
            currentStepFieldIds,
            filledFieldsFromOtherStepsCount: otherStepFilledFieldIds.length,
            filledFieldsFromOtherStepsIds: otherStepFilledFieldIds,
            fieldsSummary: summarizeFieldInTicketsForDebug(allFields),
          })
          setTicket({
            ...normalized,
            fieldInTickets: allFields
          })
          lastPersistedWriteIntegrationSnapshotRef.current = serializeWriteIntegrationSnapshot(allFields)

          const stepResponse = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, {
            params: { pipelineId: response.data.data.step?.pipelineId }
          })
          if (stepResponse.data) {
            setSteps(stepResponse.data.data || [])
          }
        }
      } catch (error) {
        console.error('[ViewOrEditTicket] fetchTicket error:', error)
      }
    }

    const fetchTags = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag`)
        if (response.data) {
          const tagsData = response.data.data || []
          console.debug('[ViewOrEditTicket] fetchTags:', tagsData)
          setAllTags(tagsData)
        }
      } catch (error) {
        console.error('[ViewOrEditTicket] fetchTags error:', error)
      }
    }

    fetchTicket()
    fetchTags()
  }, [ticketId, getActivePipelineFieldIdsByStep, hasFieldValue, serializeWriteIntegrationSnapshot, summarizeFieldInTicketsForDebug])

  useEffect(() => {
    if (!ticketId || !ticket || ticket.parentTicketId) return
    const fetchTimeline = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket-dispatch/sub-tickets/${ticketId}/timeline`)
        setSubTicketsTimeline(response.data?.data || [])
      } catch (err) {
        console.error('[ViewOrEditTicket] erro ao buscar timeline sub-tickets:', err)
      }
    }
    fetchTimeline()
  }, [ticketId, ticket?.parentTicketId])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR')
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const isImageAttachment = (attachment?: Partial<Attachment>) => {
    if (!attachment) return false

    const mimeType = String(attachment.mimeType || '').toLowerCase()
    if (mimeType.startsWith('image/')) return true

    const baseName = String(attachment.originalName || attachment.fileName || attachment.filePath || '').split('?')[0].toLowerCase()
    return /\.(png|jpe?g|gif|webp|bmp|svg|tiff?|avif|heic|heif)$/i.test(baseName)
  }

  const isPdfAttachment = (attachment?: Partial<Attachment>) => {
    if (!attachment) return false

    const mimeType = String(attachment.mimeType || '').toLowerCase()
    if (mimeType === 'application/pdf') return true

    const baseName = String(attachment.originalName || attachment.fileName || attachment.filePath || '').split('?')[0].toLowerCase()
    return /\.pdf$/i.test(baseName)
  }

  const getAttachmentContentBase64 = async (attachment: Attachment) => {
    const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachment.id}/content-base64`)
    return String(response.data?.data?.base64 || '')
  }

  const convertPdfFirstPageToImageBase64 = async (pdfBase64: string) => {
    const pdfjs = await import('pdfjs-dist') as PdfJsModule
    const cleanedBase64 = String(pdfBase64 || '').includes(',') ? String(pdfBase64).split(',')[1] : String(pdfBase64)
    const binary = atob(cleanedBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    const loadingTask = pdfjs.getDocument({ data: bytes })
    const pdfDocument = await loadingTask.promise
    const firstPage = await pdfDocument.getPage(1)
    const viewport = firstPage.getViewport({ scale: 1.6 })

    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.floor(viewport.width))
    canvas.height = Math.max(1, Math.floor(viewport.height))

    const context = canvas.getContext('2d')
    if (!context) {
      pdfDocument.destroy()
      throw new Error('Nao foi possivel gerar preview do PDF')
    }

    await firstPage.render({ canvasContext: context, viewport }).promise
    const base64Image = canvas.toDataURL('image/png')
    pdfDocument.destroy()
    return base64Image
  }

  const buildPdfCoverFileName = (attachment: Attachment) => {
    const originalName = String(attachment.originalName || attachment.fileName || 'documento')
    const baseName = originalName.replace(/\.[^/.]+$/, '').trim()
    const sanitizedBase = baseName || 'documento'
    return `${sanitizedBase}-capa.png`
  }

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return '🖼️'
    if (mimeType.startsWith('video/')) return '🎬'
    if (mimeType === 'application/pdf') return '📄'
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊'
    if (mimeType.includes('document') || mimeType.includes('word')) return '📝'
    return '📁'
  }

  const isRequiredFieldMissing = (f: FieldInTicket) => {
    if (!f.isRequired && !f.pipelineField.integrationConfig?.isRequired) return false
    const typeName = f.pipelineField.typeField?.name || ''
    const hasIntegration = !!f.pipelineField.integrationConfig
    const isProgressiveWriteIntegration = f.pipelineField.integrationConfig?.integrationType === 'write'
    if (hasIntegration) return !isProgressiveWriteIntegration && !f.integerValue
    if (['E-mail', 'CPF', 'CNPJ', 'Celular', 'Telefone', 'Texto simples'].includes(typeName)) return !f.stringValue || f.stringValue.trim() === ''
    if (typeName === 'Data') return !f.dateTimeValue
    if (typeName === 'Checkbox') return f.booleanValue === undefined
    if (typeName === 'Valor (R$)') return f.decimalValue === undefined || f.decimalValue === null
    return false
  }

  const handleSave = async () => {
    if (!ticket) return

    const saveToken = Date.now()
    writeRegistrationAutoCreateActivityByTokenRef.current.set(saveToken, 0)
    setWriteRegistrationCreateOnSaveToken(saveToken)
    await waitForWriteRegistrationAutoCreateByToken(saveToken)

    const ticketSnapshot = ticketRef.current || ticket
    if (!ticketSnapshot) return

    const formatErrors: string[] = []
    const missingRequiredFields: { id: number; name: string }[] = []
    const isEmail = (v: string) => /.+@.+\..+/.test(v)
    const onlyDigits = (v: string) => v.replace(/\D/g, '')
    const isCpfValid = (v: string) => onlyDigits(v).length === 11
    const isCnpjValid = (v: string) => onlyDigits(v).length === 14
    const isCelularValid = (v: string) => {
      const d = onlyDigits(v)
      return d.length >= 10 && d.length <= 11
    }
    const isTelefoneValid = (v: string) => onlyDigits(v).length === 10

    if (ticketSnapshot.fieldInTickets && Array.isArray(ticketSnapshot.fieldInTickets)) ticketSnapshot.fieldInTickets.forEach((f) => {
      const typeName = f.pipelineField.typeField?.name || ''

      if (isRequiredFieldMissing(f)) {
        missingRequiredFields.push({ id: Number(f.pipelineFieldId), name: f.pipelineField.name })
      }

      if (typeName === 'E-mail' && f.stringValue && !isEmail(f.stringValue)) formatErrors.push(`E-mail inválido em ${f.pipelineField.name}`)
      if (typeName === 'CPF' && f.stringValue && !isCpfValid(f.stringValue)) formatErrors.push(`CPF inválido em ${f.pipelineField.name}`)
      if (typeName === 'CNPJ' && f.stringValue && !isCnpjValid(f.stringValue)) formatErrors.push(`CNPJ inválido em ${f.pipelineField.name}`)
      if (typeName === 'Celular' && f.stringValue && !isCelularValid(f.stringValue)) formatErrors.push(`Celular inválido em ${f.pipelineField.name}`)
      if (typeName === 'Telefone' && f.stringValue && !isTelefoneValid(f.stringValue)) formatErrors.push(`Telefone inválido em ${f.pipelineField.name}`)
    })

    if (missingRequiredFields.length > 0 || formatErrors.length > 0) {
      const feedbackParts: string[] = []
      if (missingRequiredFields.length > 0) {
        feedbackParts.push(`Preencha os campos obrigatórios: ${missingRequiredFields.map((field) => field.name).join(', ')}`)
      }
      if (formatErrors.length > 0) {
        feedbackParts.push(formatErrors.join('. '))
      }
      setFeedback({ isOpen: true, message: feedbackParts.join('. '), statusOk: false, timeout: 20000 })
      return
    }

    try {
      const formData = new FormData();

      const integratedFieldPayloadPreview = (ticketSnapshot.fieldInTickets || [])
        .filter((field: any) => !!field?.pipelineField?.integrationConfig)
        .map((field: any) => ({
          pipelineFieldId: Number(field.pipelineFieldId),
          integerValue: field.integerValue,
          stringValue: field.stringValue,
          bigStringValue: field.bigStringValue,
          integrationDataId: field.integrationData?.id,
          integrationDataSelectionsCount: Array.isArray(field.integrationDataSelections) ? field.integrationDataSelections.length : 0,
          integrationDataSelections: Array.isArray(field.integrationDataSelections)
            ? field.integrationDataSelections.map((selection: any) => ({ id: selection?.id, name: selection?.name }))
            : []
        }))

      console.debug('[PIPELINE-INTEGRATED-MULTI] handleSave payload preview', {
        ticketId,
        totalFieldInTickets: Array.isArray(ticketSnapshot.fieldInTickets) ? ticketSnapshot.fieldInTickets.length : 0,
        integratedFieldPayloadPreview
      })

      const jsonData = {
        title: ticketSnapshot.title,
        description: ticketSnapshot.description,
        deadline: ticketSnapshot.deadline,
        urgency: ticketSnapshot.urgency,
        fieldInTickets: ticketSnapshot.fieldInTickets
      };

      Object.entries(jsonData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (key === 'fieldInTickets') {
          formData.append(key, JSON.stringify(value));
        } else if (value instanceof Date) {
          formData.append(key, value.toISOString());
        } else {
          formData.append(key, String(value));
        }
      });

      const response = await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data) {
        closeTicketModal()
      }
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || 'Erro ao salvar. Tente novamente.'
      setFeedback({ isOpen: true, message: errorMessage, statusOk: false })
    }
  }

  const handleStepChange = async (newStepId: number) => {
    if (!ticket || newStepId === ticket.stepId) return

    const targetStep = steps.find((s) => s.id === newStepId)
    if (moduleType === 'production' && targetStep?.allowCardCreation === false) {
      setFeedback({ isOpen: true, message: 'Não é possível mover card para esta etapa porque a adição de cards está desabilitada.', statusOk: false })
      return
    }

    try {
      const allFieldsPayload = Array.isArray(ticket.fieldInTickets) ? ticket.fieldInTickets : []
      const beforeMoveSummary = summarizeFieldInTicketsForDebug(allFieldsPayload)
      const beforeMoveFilledIds = beforeMoveSummary
        .filter((fieldSummary) => fieldSummary.hasStringValue || fieldSummary.hasIntegerValue || fieldSummary.hasDecimalValue || fieldSummary.hasDateTimeValue || fieldSummary.hasBooleanValue || fieldSummary.progressiveDraft.nonEmptyDraftCount > 0)
        .map((fieldSummary) => Number(fieldSummary.pipelineFieldId))

      console.debug('[PIPELINE-FIELDS-GLOBAL] handleStepChange payload:', {
        ticketId,
        sourceStepId: ticket.stepId,
        targetStepId: newStepId,
        payloadFieldCount: allFieldsPayload.length,
        payloadFieldIds: allFieldsPayload.map((field) => Number(field.pipelineFieldId)),
        beforeMoveFilledIds,
        beforeMoveSummary,
      })

      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`,
        {
          title: ticket.title,
          description: ticket.description,
          deadline: ticket.deadline,
          urgency: ticket.urgency,
          fieldInTickets: allFieldsPayload,
          stepId: newStepId
        }
      )

      const refreshedTicketResponse = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
      const normalized = normalizeFullTicket(refreshedTicketResponse.data?.data)
      const refreshedFields: FieldInTicket[] = Array.isArray(normalized?.fieldInTickets) ? normalized.fieldInTickets : []
      const refreshedStepId = Number(normalized?.stepId || refreshedTicketResponse.data?.data?.stepId || newStepId)

      let refreshedActiveFieldIds = new Set<number>()
      if (Number.isFinite(refreshedStepId)) {
        refreshedActiveFieldIds = await getActivePipelineFieldIdsByStep(refreshedStepId)
      }

      setActiveFieldIdsByCurrentStep(refreshedActiveFieldIds)
      setTicket({
        ...normalized,
        fieldInTickets: refreshedFields
      })

      const refreshedOtherStepFilledFieldIds = refreshedFields
        .filter((field) => !refreshedActiveFieldIds.has(Number(field.pipelineFieldId)))
        .filter((field) => hasFieldValue(field))
        .map((field) => Number(field.pipelineFieldId))

      const refreshedSummary = summarizeFieldInTicketsForDebug(refreshedFields)
      const refreshedFilledIds = refreshedSummary
        .filter((fieldSummary) => fieldSummary.hasStringValue || fieldSummary.hasIntegerValue || fieldSummary.hasDecimalValue || fieldSummary.hasDateTimeValue || fieldSummary.hasBooleanValue || fieldSummary.progressiveDraft.nonEmptyDraftCount > 0)
        .map((fieldSummary) => Number(fieldSummary.pipelineFieldId))

      const lostFieldIdsAfterMove = beforeMoveFilledIds.filter((fieldId) => !refreshedFilledIds.includes(fieldId))

      console.debug('[PIPELINE-FIELDS-GLOBAL] handleStepChange refreshed ticket:', {
        ticketId,
        refreshedStepId,
        refreshedFieldCount: refreshedFields.length,
        refreshedFieldIds: refreshedFields.map((field) => Number(field.pipelineFieldId)),
        refreshedFilledFieldsFromOtherStepsCount: refreshedOtherStepFilledFieldIds.length,
        refreshedFilledFieldsFromOtherStepsIds: refreshedOtherStepFilledFieldIds,
        refreshedFilledIds,
        lostFieldIdsAfterMove,
        refreshedSummary,
      })
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || 'Erro ao mudar de etapa. Tente novamente.'
      setFeedback({ isOpen: true, message: errorMessage, statusOk: false })
    }
  }

  const handleAddTag = async (tagId: number) => {
    if (!ticket) return

    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tags/${tagId}`)
      if (response.data?.data) {
        const newTag = normalizeTagInTicket(response.data.data)
        setTicket(prev => prev ? {
          ...prev,
          tags: [...(prev.tags ?? []), newTag]
        } : null)
      }
      setShowTagDropdown(false)
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveTag = async (tagId: number) => {
    if (!ticket) return

    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tags/${tagId}`)
      setTicket(prev => prev ? {
        ...prev,
        tags: prev.tags?.filter(t => (t.tagId ?? t.tag?.id ?? -1) !== tagId) ?? []
      } : null)
    } catch (error) {
      console.error(error)
    }
  }

  const getSelectedTagIds = () => {
    if (!ticket) return []

    return (ticket.tags || []).map(t => (t?.tagId ?? t?.tag?.id ?? -1)).filter(id => id !== -1)
  }

  const getAvailableTags = () => {
    const selectedTagIds = getSelectedTagIds()

    if (!ticket) {
      console.debug('[ViewOrEditTicket] getAvailableTags (ticket vazio):', allTags)
      return allTags
    }

    const available = allTags.filter(tag => !selectedTagIds.includes(tag.id))
    console.debug('[ViewOrEditTicket] ticket.tags:', ticket.tags)
    console.debug('[ViewOrEditTicket] allTags:', allTags)
    console.debug('[ViewOrEditTicket] selectedTagIds:', selectedTagIds)
    console.debug('[ViewOrEditTicket] available tags:', available)
    return available
  }

  const updateNewTaskTitleForList = (taskListId: number, value: string) => {
    setNewTaskTitleByListId((previousState) => ({
      ...previousState,
      [taskListId]: value,
    }))
  }

  const handleAddTaskList = async () => {
    const normalizedName = newTaskListName.trim()
    if (!normalizedName) return

    try {
      await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/task-lists`, {
        name: normalizedName,
      })
      setNewTaskListName('')
      setShowAddTaskList(false)
      await refreshTicketDetails()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeleteTaskList = async (taskListId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/task-lists/${taskListId}`)
      setEditingTaskListId((currentTaskListId) => (currentTaskListId === taskListId ? null : currentTaskListId))
      setEditingTaskListName('')
      setActiveTaskListForNewTask((currentTaskListId) => (currentTaskListId === taskListId ? null : currentTaskListId))
      setNewTaskTitleByListId((previousState) => {
        const nextState = { ...previousState }
        delete nextState[taskListId]
        return nextState
      })
      await refreshTicketDetails()
    } catch (error) {
      console.error(error)
    }
  }

  const handleStartRenameTaskList = (taskListId: number, currentName: string) => {
    setEditingTaskListId(taskListId)
    setEditingTaskListName(currentName)
  }

  const handleCancelRenameTaskList = () => {
    setEditingTaskListId(null)
    setEditingTaskListName('')
  }

  const handleRenameTaskList = async (taskListId: number) => {
    const normalizedName = editingTaskListName.trim()
    if (!normalizedName) return

    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/task-lists/${taskListId}`, {
        name: normalizedName,
      })
      handleCancelRenameTaskList()
      await refreshTicketDetails()
    } catch (error) {
      console.error(error)
    }
  }

  const handleToggleTask = async (taskId: number, completed: boolean) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tasks/${taskId}`, {
        completed: !completed
      })
      await refreshTicketDetails()
    } catch (error) {
      console.error(error)
    }
  }

  const handleAddTask = async (taskListId: number) => {
    const newTaskTitle = String(newTaskTitleByListId[taskListId] || '').trim()
    if (!newTaskTitle) return

    try {
      await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/task-lists/${taskListId}/tasks`, {
        title: newTaskTitle
      })
      updateNewTaskTitleForList(taskListId, '')
      await refreshTicketDetails()
      newTaskInputRef.current?.focus()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeleteTask = async (taskId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tasks/${taskId}`)
      setTaskMenuId(null)
      await refreshTicketDetails()
    } catch (error) {
      console.error(error)
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const attachments: { fileName: string; file: string }[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.readAsDataURL(file)
      })
      attachments.push({ fileName: file.name, file: base64 })
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments`, {
        attachments
      })
      if (response.data) {
        setTicket(prev => prev ? {
          ...prev,
          attachments: [...prev.attachments, ...response.data.data]
        } : null)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleViewAttachment = async (attachment: Attachment) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachment.id}/view`)
      window.open(response.data.url, '_blank')
    } catch (error) {
      console.error(error)
    }
    setAttachmentMenuId(null)
  }

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachment.id}/download`)
      const link = document.createElement('a')
      link.href = response.data.url
      link.download = attachment.originalName
      link.click()
    } catch (error) {
      console.error(error)
    }
    setAttachmentMenuId(null)
  }

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachmentId}`)
      setTicket(prev => prev ? {
        ...prev,
        attachments: prev.attachments.filter(a => a.id !== attachmentId)
      } : null)
      setAttachmentMenuId(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleSetCover = async (fileId: number) => {
    if (isGeneratingPdfCover) return

    try {
      const selectedAttachment = ticket?.attachments.find((attachment) => attachment.id === fileId)
      if (!selectedAttachment) {
        throw new Error('Anexo nao encontrado para definir capa')
      }

      if (isPdfAttachment(selectedAttachment)) {
        setIsGeneratingPdfCover(true)
        const pdfBase64 = await getAttachmentContentBase64(selectedAttachment)
        if (!pdfBase64) {
          throw new Error('Nao foi possivel obter o conteudo do PDF')
        }
        const coverBase64 = await convertPdfFirstPageToImageBase64(pdfBase64)
        await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/cover/generated`, {
          fileName: buildPdfCoverFileName(selectedAttachment),
          file: coverBase64,
        })
      } else {
        await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/cover/${fileId}`)
      }

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
      if (response.data) {
        setTicket(normalizeFullTicket(response.data.data))
      }
      setAttachmentMenuId(null)
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Nao foi possivel definir a capa'
      setFeedback({ isOpen: true, message: errorMessage, statusOk: false })
    } finally {
      setIsGeneratingPdfCover(false)
    }
  }

  const handleRemoveCover = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/cover`)

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
      if (response.data) {
        setTicket(normalizeFullTicket(response.data.data))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleToggleOptionsMenu = () => {
    if (showOptionsMenu) {
      setShowOptionsMenu(false)
      return
    }
    if (optionsButtonRef.current) {
      const rect = optionsButtonRef.current.getBoundingClientRect()
      const menuWidth = 160
      const buttonCenter = rect.left + rect.width / 2
      let left = buttonCenter - menuWidth / 2
      const top = rect.bottom + 8
      if (left + menuWidth > window.innerWidth - 10) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      setOptionsMenuPosition({ top, left })
    }
    setShowOptionsMenu(true)
  }

  const handleDuplicateTicket = async () => {
    if (!ticket) return
    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: `${ticket.title} (cópia)`,
        stepId: ticket.stepId
      })
      if (response.data) {
        setShowOptionsMenu(false)
        navigate(buildTicketRoute(response.data.data.id))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleCopyLink = () => {
    const targetTicketId = ticket?.id ?? ticketId
    if (!targetTicketId) return
    const url = `${window.location.origin}${buildTicketRoute(targetTicketId)}`
    navigator.clipboard.writeText(url)
    setFeedback({ isOpen: true, message: 'Link copiado para a área de transferência', statusOk: true })
    setShowOptionsMenu(false)
  }

  const selectedTagIds = getSelectedTagIds()

  const handleStartRename = () => {
    if (!ticket) return
    setTitleDraft(ticket.title)
    setIsEditingTitle(true)
    setShowOptionsMenu(false)
  }

  const handleConfirmRename = async () => {
    if (!ticket || !titleDraft.trim()) return
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        title: titleDraft.trim()
      })
      setTicket({ ...ticket, title: titleDraft.trim() })
      setIsEditingTitle(false)
    } catch (error) {
      console.error('Erro ao renomear ticket:', error)
    }
  }

  const handleCancelRename = () => {
    setIsEditingTitle(false)
    setTitleDraft('')
  }

  const handleArchiveTicket = async () => {
    if (!ticket) return
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/archive`)
      setShowOptionsMenu(false)
      closeTicketModal()
    } catch (error) {
      console.error('Erro ao arquivar ticket:', error)
    }
  }

  const handleDeleteTicket = async () => {
    if (!ticket) return
    if (!window.confirm('Tem certeza que deseja excluir este card?')) return
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`)
      setShowOptionsMenu(false)
      closeTicketModal()
    } catch (error) {
      console.error('Erro ao excluir ticket:', error)
    }
  }

  const handleOpenSendToPipeline = async () => {
    setShowOptionsMenu(false)
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`)
      const pipelines = (response.data?.data || []).filter((p: any) => p.id !== ticket?.step?.pipelineId)
      setAvailablePipelines(pipelines)
      setSelectedTargetPipelineId(null)
      setAvailableTargetSteps([])
      setSelectedTargetStepId(null)
      setShowSendToPipelineModal(true)
    } catch (err) {
      console.error('Erro ao buscar pipelines:', err)
    }
  }

  const handleSelectTargetPipeline = async (pipelineId: number | null) => {
    setSelectedTargetPipelineId(pipelineId)
    setSelectedTargetStepId(null)
    setAvailableTargetSteps([])
    if (!pipelineId) return
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, {
        params: { pipelineId }
      })
      setAvailableTargetSteps(response.data?.data || [])
    } catch (err) {
      console.error('Erro ao buscar etapas do funil destino:', err)
    }
  }

  const handleConfirmSendToPipeline = async () => {
    if (!selectedTargetPipelineId || !ticketId) return
    try {
      setSendingToPipeline(true)
      await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket-dispatch/send/${ticketId}`, {
        targetPipelineId: selectedTargetPipelineId,
        ...(selectedTargetStepId ? { targetStepId: selectedTargetStepId } : {})
      })
      setShowSendToPipelineModal(false)
      setFeedback({ isOpen: true, message: 'Subcard enviado com sucesso!', statusOk: true })
    } catch (err: any) {
      setFeedback({ isOpen: true, message: err?.response?.data?.message || 'Erro ao enviar o subcard', statusOk: false })
    } finally {
      setSendingToPipeline(false)
    }
  }

  const handleFinishSubcard = async () => {
    if (!ticket?.TicketDispatchAsChild || finishingSubcard) return
    try {
      setFinishingSubcard(true)
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket-dispatch/${ticket.TicketDispatchAsChild.id}/complete`)
      setShowFinishSubcardModal(false)
      setFeedback({ isOpen: true, message: 'Subcard finalizado e enviado de volta ao card original!', statusOk: true })
      closeTicketModal()
    } catch (err: any) {
      setShowFinishSubcardModal(false)
      setFeedback({ isOpen: true, message: err?.response?.data?.message || 'Erro ao finalizar o subcard', statusOk: false })
    } finally {
      setFinishingSubcard(false)
    }
  }

  const handleOpenSubTicket = (entry: any) => {
    if (!entry?.childTicketId) return
    setPreviewSubTicketId(Number(entry.childTicketId))
  }

  const cancelHideSubTicketStepTooltip = () => {
    if (subTicketTooltipHideTimeoutRef.current) {
      clearTimeout(subTicketTooltipHideTimeoutRef.current)
      subTicketTooltipHideTimeoutRef.current = null
    }
  }

  const showSubTicketStepTooltip = (event: React.MouseEvent<HTMLElement>, step: any) => {
    cancelHideSubTicketStepTooltip()
    const rect = event.currentTarget.getBoundingClientRect()
    const tooltipWidth = 290
    const centerX = rect.left + rect.width / 2
    const left = Math.max(8, Math.min(centerX - tooltipWidth / 2, window.innerWidth - tooltipWidth - 8))
    const arrowLeft = Math.max(14, Math.min(centerX - left, tooltipWidth - 14))
    const below = rect.top < 300
    const top = below ? rect.bottom + 10 : rect.top - 10
    setSubTicketStepTooltip({ step, top, left, arrowLeft, below })
  }

  const scheduleHideSubTicketStepTooltip = () => {
    cancelHideSubTicketStepTooltip()
    subTicketTooltipHideTimeoutRef.current = setTimeout(() => setSubTicketStepTooltip(null), 150)
  }

  const handleSetTicketStatus = async (status: 'GANHOU' | 'PERDEU') => {
    if (!ticket) return
    const nextStatus: 'NEUTRO' | 'GANHOU' | 'PERDEU' = ticket.status === status ? 'NEUTRO' : status
    const shouldDisableUrgency = nextStatus !== 'NEUTRO'

    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        status: nextStatus,
        ...(shouldDisableUrgency ? { urgency: false } : {})
      })
      setTicket({
        ...ticket,
        status: nextStatus,
        urgency: shouldDisableUrgency ? false : ticket.urgency,
      })
    } catch (error) {
      console.error('Erro ao atualizar status do ticket:', error)
    }
  }

  const handleToggleSalesOrder = async (nextStatus: boolean | null) => {
    if (!ticket || !ticket.externalId) return
    const nextSaleStatus = nextStatus === true ? 'closed' : (nextStatus === false ? 'cancelled' : 'open')
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order/${ticket.externalId}`, {
        status: nextStatus
      })
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        saleStatus: nextSaleStatus
      })
      setTicket({
        ...ticket,
        saleStatus: nextSaleStatus
      })
    } catch (error) {
      console.error('Erro ao atualizar status da venda:', error)
    }
  }

  const isWon = ticket?.status === 'GANHOU'
  const isLost = ticket?.status === 'PERDEU'
  const isNeutralStatus = !ticket?.status || ticket.status === 'NEUTRO'

  const returnTo = searchParams.get('returnTo') || ''
  const returnToLower = returnTo.toLowerCase()
  const isProductionModuleView =
    (!!pipelineContext?.isModuleView && pipelineContext?.moduleType === 'production') ||
    returnToLower.includes('production')
  const isProductionTicket = ticket?.step?.pipeline?.moduleType === 'production' && !!ticket?.externalId
  const shouldShowProductionTimeline = isProductionModuleView || isProductionTicket
  const isOrderModuleView =
    (!!pipelineContext?.isModuleView && pipelineContext?.moduleType === 'sales') ||
    (!isProductionModuleView && (returnToLower.includes('steel') || returnToLower.includes('fashion') || returnToLower.includes('orders') || returnToLower.includes('sales')))

  const isProductDevelopmentModuleView =
    (!!pipelineContext?.isModuleView && pipelineContext?.moduleType === 'product_development') ||
    returnToLower.includes('/collection/funnel')

  const subModule =
    pipelineContext?.subModule ||
    (returnToLower.includes('fashion') ? 'fashion' : (returnToLower.includes('steel') ? 'steel' : undefined))

  const derivedStatusFromSale = useMemo(() => {
    if (!isOrderModuleView || !ticket) return null
    const sale = ticket.saleStatus
    if (sale === 'closed' || sale === 'won') return 'GANHOU'
    if (sale === 'cancelled' || sale === 'lost') return 'PERDEU'
    return 'NEUTRO'
  }, [isOrderModuleView, ticket?.saleStatus])

  const effectiveIsWon = isOrderModuleView ? derivedStatusFromSale === 'GANHOU' : isWon
  const effectiveIsLost = isOrderModuleView ? derivedStatusFromSale === 'PERDEU' : isLost

  if (!ticket) return null

  return (
    <FormProvider {...methods}>
      <S.Overlay $preview={isPreview} onClick={isPreview ? onClosePreview : closeTicketModal} />
      <S.Container $preview={isPreview}>
        <S.Header>
          <S.HeaderLeft>
            {isEditingTitle ? (
              <S.TitleEditWrapper>
                <S.TitleInput
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirmRename()
                    if (e.key === 'Escape') handleCancelRename()
                  }}
                  autoFocus
                />
                <S.TitleEditButtons>
                  <button onClick={handleConfirmRename}>Salvar</button>
                  <button onClick={handleCancelRename} className="cancel">Cancelar</button>
                </S.TitleEditButtons>
              </S.TitleEditWrapper>
            ) : (
              <>
                <S.Title>{ticket.title}</S.Title>
                <S.CreatedAt>Criado em {formatDate(ticket.createdAt)}</S.CreatedAt>
              </>
            )}
          </S.HeaderLeft>
          <S.HeaderRight>
            {!isPreview && (
            <S.TicketStatusActions>
              <S.TicketStatusButton
                $active={effectiveIsWon}
                $tone='positive'
                onClick={isOrderModuleView ? () => handleToggleSalesOrder(ticket.saleStatus === 'closed' || ticket.saleStatus === 'won' ? null : true) : () => handleSetTicketStatus('GANHOU')}
                title={isOrderModuleView ? (effectiveIsWon ? 'Reabrir venda' : 'Fechar venda') : 'Marcar como ganhou'}
              >
                <ThumbsUp size={16} />
              </S.TicketStatusButton>
              <S.TicketStatusButton
                $active={effectiveIsLost}
                $tone='negative'
                onClick={isOrderModuleView ? () => handleToggleSalesOrder(ticket.saleStatus === 'cancelled' || ticket.saleStatus === 'lost' ? null : false) : () => handleSetTicketStatus('PERDEU')}
                title={isOrderModuleView ? (effectiveIsLost ? 'Reabrir venda' : 'Cancelar venda') : 'Marcar como perdeu'}
              >
                <ThumbsDown size={16} />
              </S.TicketStatusButton>
            </S.TicketStatusActions>
            )}
            {!isPreview && (
            <S.OptionsButton ref={optionsButtonRef} onClick={handleToggleOptionsMenu}>
              <MoreHorizontal size={18} />
            </S.OptionsButton>
            )}
            {showOptionsMenu && ReactDOM.createPortal(
              <S.AttachmentDropdownPortal
                ref={optionsMenuRef}
                style={{ top: optionsMenuPosition.top, left: optionsMenuPosition.left }}
                onClick={(e) => e.stopPropagation()}
              >
                {isProductDevelopmentModuleView && ticket.externalId && (
                <S.DropdownItem onClick={() => {
                  const currentUrl = `/panel/pipeline/ticket?${searchParams.toString()}`
                  navigate(`/panel/composition-product/edit/${ticket.externalId}?returnTo=${encodeURIComponent(currentUrl)}`)
                  setShowOptionsMenu(false)
                }}>
                  <ExternalLink size={16} /> Abrir produto
                </S.DropdownItem>
                )}
                {isProductionModuleView && subModule === 'fashion' && ticket.externalId && (
                <S.DropdownItem onClick={() => {
                  const currentUrl = `/panel/pipeline/ticket?${searchParams.toString()}`
                  navigate(`/panel/production-fashion?opId=${ticket.externalId}&returnTo=${encodeURIComponent(currentUrl)}`)
                  setShowOptionsMenu(false)
                }}>
                  <ExternalLink size={16} /> Abrir OP
                </S.DropdownItem>
                )}
                {isProductionModuleView && (subModule === 'steel' || !subModule) && ticket.externalId && (
                <S.DropdownItem onClick={() => {
                  const currentUrl = `/panel/pipeline/ticket?${searchParams.toString()}`
                  navigate(`/panel/production/edit/${ticket.externalId}?returnTo=${encodeURIComponent(currentUrl)}`)
                  setShowOptionsMenu(false)
                }}>
                  <ExternalLink size={16} /> Abrir OP
                </S.DropdownItem>
                )}
                {isOrderModuleView && subModule === 'fashion' && ticket.externalId && (
                <S.DropdownItem onClick={() => {
                  const currentUrl = `/panel/pipeline/ticket?${searchParams.toString()}`
                  navigate(`/panel/fashion/edit/${ticket.externalId}?returnTo=${encodeURIComponent(currentUrl)}`)
                  setShowOptionsMenu(false)
                }}>
                  <ExternalLink size={16} /> Abrir pedido
                </S.DropdownItem>
                )}
                {isOrderModuleView && (subModule === 'steel' || !subModule) && ticket.externalId && (
                <S.DropdownItem onClick={() => {
                  const currentUrl = `/panel/pipeline/ticket?${searchParams.toString()}`
                  navigate(`/panel/steel/edit/${ticket.externalId}?returnTo=${encodeURIComponent(currentUrl)}`)
                  setShowOptionsMenu(false)
                }}>
                  <ExternalLink size={16} /> Abrir pedido
                </S.DropdownItem>
                )}
                <S.DropdownItem onClick={handleDuplicateTicket}>
                  <Copy size={16} /> Duplicar
                </S.DropdownItem>
                <S.DropdownItem onClick={handleStartRename}>
                  <Pencil size={16} /> Renomear
                </S.DropdownItem>
                <S.DropdownItem onClick={handleCopyLink}>
                  <Link2 size={16} /> Copiar link
                </S.DropdownItem>
                <S.DropdownItem onClick={handleArchiveTicket}>
                  <Archive size={16} /> Arquivar
                </S.DropdownItem>
                <S.DropdownItem className="danger" onClick={handleDeleteTicket}>
                  <Trash2 size={16} /> Excluir
                </S.DropdownItem>
              </S.AttachmentDropdownPortal>,
              document.body
            )}
            {!isPreview && !ticket.parentTicketId && (
              <S.SendSubcardButton onClick={handleOpenSendToPipeline}>
                <ExternalLink size={16} />
                Enviar subcard
              </S.SendSubcardButton>
            )}
            {!isPreview && ticket.parentTicketId && ticket.TicketDispatchAsChild?.status === 'APPROVED' && (
              <S.FinishSubcardButton onClick={() => setShowFinishSubcardModal(true)}>
                <CheckCircle size={16} />
                Finalizar
              </S.FinishSubcardButton>
            )}
            {isPreview ? (
              <S.SaveButton onClick={onClosePreview}>
                <ArrowLeft size={16} />
                Voltar
              </S.SaveButton>
            ) : (
              <S.SaveButton onClick={handleSave}>
                <Calendar size={16} />
                Salvar e fechar
              </S.SaveButton>
            )}
          </S.HeaderRight>
        </S.Header>

        <S.Content $readOnly={isPreview}>
          {ticket.parentTicketId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '8px 12px', background: '#EBF7FD', borderRadius: '6px', border: '1px solid #0090D3' }}>
              <span style={{ background: '#0090D3', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '3px' }}>Subcard</span>
              {ticket.ParentTicket && (
                <span style={{ fontSize: '12px', color: '#333' }}>Card pai: <span onClick={() => setPreviewSubTicketId(Number(ticket.ParentTicket!.id))} style={{ color: '#0090D3', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>{ticket.ParentTicket.title}</span></span>
              )}
            </div>
          )}
          {coverImage && (
            <div style={{ width: '100%', marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', position: 'relative', maxHeight: '400px' }}>
              <img
                src={coverImage}
                alt="Capa do Ticket"
                style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain', display: 'block' }}
              />
              <button
                onClick={handleRemoveCover}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px'
                }}
              >
                ×
              </button>
            </div>
          )}

          <S.FormGrid>
            <S.Label>Etapa</S.Label>
            <S.SelectInput>
              <select
                value={ticket.stepId}
                onChange={(e) => handleStepChange(Number(e.target.value))}
              >
                {steps.map(step => (
                  <option key={step.id} value={step.id}>{step.name}</option>
                ))}
              </select>
            </S.SelectInput>

            <S.Label>Tags</S.Label>
            <S.TagsInputContainer
              ref={tagContainerRef}
              onClick={() => {
                if (tagContainerRef.current) {
                  const rect = tagContainerRef.current.getBoundingClientRect()
                  const width = Math.min(300, rect.width)
                  const top = rect.bottom + 6
                  const left = Math.max(8, rect.left)
                  setTagDropdownCoords({ top, left, width })
                }
                setShowTagDropdown(prev => !prev)
              }}
            >
              {(ticket.tags || []).map(tagInTicket => {
                const tagObj = tagInTicket.tag || {} as any
                const tagId = tagInTicket.tagId ?? tagObj.id ?? -1
                const tagColor = tagObj.color || '#999'
                const tagName = String(tagObj.name || 'Tag')

                return (
                  <S.Tag key={tagId} color={tagColor} onClick={(e) => e.stopPropagation()}>
                    {tagName}
                    <button onClick={() => handleRemoveTag(tagId)}>×</button>
                  </S.Tag>
                )
              })}
              <S.AddTagButton>
                <S.TagDropdownIcon />
              </S.AddTagButton>
              {showTagDropdown && tagDropdownCoords && (
                <S.TagDropdown
                  ref={tagDropdownRef}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'fixed',
                    top: tagDropdownCoords.top,
                    left: tagDropdownCoords.left,
                    width: tagDropdownCoords.width,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    zIndex: 1100,
                  }}
                >
                  {(allTags.length > 0 ? allTags : []).map(tag => {
                    const isSelected = selectedTagIds.includes(tag.id)
                    return (
                      <S.TagDropdownItem
                        key={tag.id}
                        onClick={() => {
                          if (isSelected) {
                            handleRemoveTag(tag.id)
                          } else {
                            handleAddTag(tag.id)
                          }
                        }}
                        style={{
                          color: isSelected ? '#0090D3' : '#303030',
                          fontWeight: isSelected ? 600 : 400,
                          background: isSelected ? '#eef7ff' : 'transparent',
                        }}
                      >
                        <S.TagColor color={tag.color || '#E5E7EB'} />
                        <span style={{ flex: 1 }}>{tag.name}</span>
                        {isSelected && <span>✓</span>}
                      </S.TagDropdownItem>
                    )
                  })}
                  {allTags.length === 0 && <S.TagDropdownEmpty>Nenhuma tag disponível</S.TagDropdownEmpty>}
                </S.TagDropdown>
              )}
            </S.TagsInputContainer>

            <S.Label>Data de vencimento</S.Label>
            <S.Input
              type="date"
              value={ticket.deadline ? new Date(ticket.deadline).toISOString().split('T')[0] : ''}
              onChange={(e) => setTicket({ ...ticket, deadline: new Date(e.target.value) })}
            />

            <S.Label>Urgente</S.Label>
            <S.CheckboxWrapper>
              <S.Checkbox
                checked={ticket.urgency || false}
                $disabled={!isNeutralStatus}
                title={isNeutralStatus ? 'Marcar como urgente' : 'Disponível apenas no status neutro'}
                onClick={() => {
                  if (!isNeutralStatus) return
                  setTicket({ ...ticket, urgency: !ticket.urgency })
                }}
              />
            </S.CheckboxWrapper>

            <S.Divider />

            {currentStepRegularFields.length > 0 ? (
              currentStepRegularFields.map((field) => (
                <RenderField
                  field={field}
                  setValue={setTicket as any}
                  key={field.pipelineFieldId}
                  isFilled={isFilledBoolean}
                  control={control}
                  isFromCurrentStep={true}
                  writeRegistrationCreateOnSaveToken={writeRegistrationCreateOnSaveToken}
                  onWriteRegistrationAutoCreateActivity={handleWriteRegistrationAutoCreateActivity}
                />
              ))
            ) : null}

            {progressiveWriteRegistrationFieldsForDisplay.length > 0 ? (
              <>
                <S.FieldSectionTitle>
                  Campos integrados de registro ({progressiveWriteRegistrationFieldsForDisplay.length})
                </S.FieldSectionTitle>
                {progressiveWriteRegistrationFieldsForDisplay.map((field) => (
                  <S.IntegratedRegistrationBlock key={`integrated-write-${field.pipelineFieldId}`}>
                    <RenderField
                      field={field}
                      setValue={setTicket as any}
                      isFilled={isFilledBoolean}
                      control={control}
                      allFieldsInTicket={ticket?.fieldInTickets || []}
                      isFromCurrentStep={true}
                      writeRegistrationCreateOnSaveToken={writeRegistrationCreateOnSaveToken}
                      onWriteRegistrationAutoCreateActivity={handleWriteRegistrationAutoCreateActivity}
                    />
                  </S.IntegratedRegistrationBlock>
                ))}
              </>
            ) : null}

            {filledFieldsFromOtherSteps.length > 0 ? (
              <>
                <S.FieldSectionTitle>
                  Campos preenchidos de outras etapas ({filledFieldsFromOtherSteps.length})
                </S.FieldSectionTitle>
                {filledFieldsFromOtherSteps.map((field) => (
                  <RenderField
                    field={field}
                    setValue={setTicket as any}
                    key={`global-${field.pipelineFieldId}`}
                    isFilled={isFilledBoolean}
                    control={control}
                    isFromCurrentStep={false}
                    writeRegistrationCreateOnSaveToken={writeRegistrationCreateOnSaveToken}
                    onWriteRegistrationAutoCreateActivity={handleWriteRegistrationAutoCreateActivity}
                  />
                ))}
              </>
            ) : null}

            <S.Divider />

            <S.DescriptionLabel>Descrição</S.DescriptionLabel>
            <S.TextArea
              value={ticket.description || ''}
              onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
              placeholder="Digite aqui"
            />
          </S.FormGrid>

          <S.SectionDivider />

          <S.Section>
            <S.SectionHeader>
              <S.SectionTitle>Anexos</S.SectionTitle>
              <S.SectionAction onClick={() => document.getElementById('file-upload-input')?.click()}>
                <Paperclip size={14} />
                Anexar arquivos
              </S.SectionAction>
              <input
                id="file-upload-input"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </S.SectionHeader>
            <S.AttachmentList>
              {ticket.attachments.map(attachment => {
                const canPreviewAsImage = isImageAttachment(attachment)
                console.log('Attachment debug:', {
                  id: attachment.id,
                  originalName: attachment.originalName,
                  mimeType: attachment.mimeType,
                  filePath: attachment.filePath,
                  isImage: canPreviewAsImage
                });
                return (
                  <S.AttachmentItem key={attachment.id}>
                    <S.AttachmentIcon onClick={() => handleViewAttachment(attachment)}>
                      <S.AttachmentMenu
                        ref={el => attachmentMenuRefs.current[attachment.id] = el}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleAttachmentMenu(attachment.id)
                        }}
                      >
                        ⋯
                      </S.AttachmentMenu>
                      {canPreviewAsImage ? (
                        <img
                          src={attachment.filePath}
                          alt={attachment.originalName}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                          onError={(e) => {
                            console.error('Erro ao carregar preview da imagem:', attachment.filePath);

                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const icon = document.createElement('div');
                              icon.innerHTML = getFileIcon(attachment.mimeType);
                              icon.style.fontSize = '24px';
                              parent.appendChild(icon);
                            }
                          }}
                        />
                      ) : (
                        <S.FileIcon>{getFileIcon(attachment.mimeType)}</S.FileIcon>
                      )}
                    </S.AttachmentIcon>
                    <S.AttachmentName title={attachment.originalName}>
                      {attachment.originalName}
                    </S.AttachmentName>
                  </S.AttachmentItem>
                );
              })}
              {attachmentMenuId !== null && ReactDOM.createPortal(
                <S.AttachmentDropdownPortal
                  ref={attachmentDropdownRef}
                  style={{ top: attachmentMenuPosition.top, left: attachmentMenuPosition.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <S.DropdownItem onClick={() => {
                    const att = ticket.attachments.find(a => a.id === attachmentMenuId)
                    if (att) handleViewAttachment(att)
                  }}>
                    Visualizar
                  </S.DropdownItem>
                  <S.DropdownItem onClick={() => {
                    const att = ticket.attachments.find(a => a.id === attachmentMenuId)
                    if (att) handleDownloadAttachment(att)
                  }}>
                    Download
                  </S.DropdownItem>
                  {(() => {
                    const att = ticket.attachments.find(a => a.id === attachmentMenuId);
                    return att && (isImageAttachment(att) || isPdfAttachment(att)) ? (
                      <S.DropdownItem onClick={() => handleSetCover(att.id)}>
                        {isGeneratingPdfCover ? 'Gerando capa...' : 'Definir como capa'}
                      </S.DropdownItem>
                    ) : null;
                  })()}
                  <S.DropdownItem className="danger" onClick={() => {
                    if (attachmentMenuId) handleDeleteAttachment(attachmentMenuId)
                  }}>
                    Excluir
                  </S.DropdownItem>
                </S.AttachmentDropdownPortal>,
                document.body
              )}
            </S.AttachmentList>
          </S.Section>

          <S.SectionDivider />

          <S.Section>
            <S.SectionHeader>
              <S.SectionTitle>Tarefas</S.SectionTitle>
              <S.SectionActions>
                <S.SectionAction onClick={() => setShowAddTaskList(true)}>
                  <Plus size={14} />
                  Adicionar lista
                </S.SectionAction>
              </S.SectionActions>
            </S.SectionHeader>
            <S.TaskList>
              {taskListsForDisplay.map(taskList => (
                <S.TaskListGroup key={`task-list-${taskList.id ?? 'sem-lista'}`}>
                  <S.TaskListGroupHeader>
                    {typeof taskList.id === 'number' && editingTaskListId === taskList.id ? (
                      <S.TaskListRenameForm>
                        <input
                          type='text'
                          value={editingTaskListName}
                          onChange={(e) => setEditingTaskListName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameTaskList(taskList.id as number)
                            if (e.key === 'Escape') handleCancelRenameTaskList()
                          }}
                          autoFocus
                        />
                        <button onClick={() => handleRenameTaskList(taskList.id as number)}>Salvar</button>
                        <button className='cancel' onClick={handleCancelRenameTaskList}>Cancelar</button>
                      </S.TaskListRenameForm>
                    ) : (
                      <S.TaskListGroupTitle>{taskList.name}</S.TaskListGroupTitle>
                    )}
                    <S.TaskListGroupActions>
                      {typeof taskList.id === 'number' && (
                        <S.TaskListInlineAction
                          onClick={() => {
                            setActiveTaskListForNewTask(taskList.id)
                          }}
                        >
                          <Plus size={12} />
                          Adicionar tarefa
                        </S.TaskListInlineAction>
                      )}
                      {typeof taskList.id === 'number' && (
                        <S.TaskListInlineAction
                          onClick={() => {
                            handleStartRenameTaskList(taskList.id as number, taskList.name)
                          }}
                        >
                          Renomear lista
                        </S.TaskListInlineAction>
                      )}
                      {typeof taskList.id === 'number' && (
                        <S.TaskListInlineAction
                          className='danger'
                          onClick={() => {
                            handleDeleteTaskList(taskList.id as number)
                          }}
                        >
                          Excluir lista
                        </S.TaskListInlineAction>
                      )}
                    </S.TaskListGroupActions>
                  </S.TaskListGroupHeader>

                  {taskList.tasks.map(task => (
                    <S.TaskItem key={task.id}>
                      <S.TaskCheckbox
                        checked={task.completed}
                        onClick={() => handleToggleTask(task.id, task.completed)}
                      />
                      <S.TaskTitle completed={task.completed}>{task.title}</S.TaskTitle>
                      <S.TaskMenu
                        ref={el => taskMenuRefs.current[task.id] = el}
                        onClick={() => handleToggleTaskMenu(task.id)}
                      >
                        <MoreHorizontal size={16} />
                      </S.TaskMenu>
                    </S.TaskItem>
                  ))}

                  {taskList.tasks.length === 0 && (
                    <S.EmptyTaskMessage>Sem tarefas nesta lista</S.EmptyTaskMessage>
                  )}

                  {typeof taskList.id === 'number' && activeTaskListForNewTask === taskList.id && (
                    <S.AddTaskInput>
                      <input
                        ref={newTaskInputRef}
                        type='text'
                        placeholder='Digite o título da tarefa'
                        value={newTaskTitleByListId[taskList.id] || ''}
                        onChange={(e) => updateNewTaskTitleForList(taskList.id as number, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTask(taskList.id as number)}
                        autoFocus
                      />
                      <button onClick={() => handleAddTask(taskList.id as number)}>Adicionar</button>
                      <button
                        onClick={() => {
                          setActiveTaskListForNewTask(null)
                          updateNewTaskTitleForList(taskList.id as number, '')
                        }}
                        style={{ background: '#999' }}
                      >
                        Cancelar
                      </button>
                    </S.AddTaskInput>
                  )}
                </S.TaskListGroup>
              ))}

              {taskListsForDisplay.length === 0 && (
                <S.EmptyTaskMessage>Nenhuma lista criada</S.EmptyTaskMessage>
              )}

              {taskMenuId !== null && ReactDOM.createPortal(
                <S.AttachmentDropdownPortal
                  ref={taskDropdownRef}
                  style={{ top: taskMenuPosition.top, left: taskMenuPosition.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <S.DropdownItem className="danger" onClick={() => {
                    if (taskMenuId) handleDeleteTask(taskMenuId)
                  }}>
                    Excluir
                  </S.DropdownItem>
                </S.AttachmentDropdownPortal>,
                document.body
              )}

              {showAddTaskList && (
                <S.AddTaskInput>
                  <input
                    type='text'
                    placeholder='Digite o nome da lista'
                    value={newTaskListName}
                    onChange={(e) => setNewTaskListName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTaskList()}
                    autoFocus
                  />
                  <button onClick={handleAddTaskList}>Adicionar</button>
                  <button onClick={() => { setShowAddTaskList(false); setNewTaskListName('') }} style={{ background: '#999' }}>
                    Cancelar
                  </button>
                </S.AddTaskInput>
              )}
            </S.TaskList>
            </S.Section>

            {shouldShowProductionTimeline && (
              <>
                <S.SectionDivider />
                <S.Section data-preview-interactive style={{ position: 'relative', zIndex: 5000 }}>
                  <S.SectionHeader>
                    <S.SectionTitle>Histórico de etapas</S.SectionTitle>
                  </S.SectionHeader>
                  <StepTimeline
                    ticketId={ticket.id}
                    isProductionView={true}
                    serviceId={ticket.externalId}
                  />
                </S.Section>
              </>
            )}

            <S.SectionDivider />

            {!ticket.parentTicketId && subTicketsTimeline.length > 0 && (
              <>
                <S.Section>
                  <S.SectionTitle>Sub-tickets enviados</S.SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {subTicketsTimeline.map(entry => {
                      const statusColors: Record<string, string> = { PENDING: '#F59E0B', APPROVED: '#0090D3', COMPLETED: '#22C55E', REJECTED: '#EF4444' }
                      const statusLabels: Record<string, string> = { PENDING: 'Pendente', APPROVED: 'Em andamento', COMPLETED: 'Devolvido', REJECTED: 'Rejeitado' }
                      return (
                        <S.SubTicketCard key={entry.dispatchId} onClick={() => handleOpenSubTicket(entry)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ background: statusColors[entry.status] || '#999', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px' }}>{statusLabels[entry.status] || entry.status}</span>
                            <span style={{ fontSize: '13px', fontWeight: 600 }}>{entry.childTicketTitle}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            Funil: <strong>{entry.targetPipelineName}</strong>
                          </div>
                          {entry.steps && entry.steps.length > 0 && (
                            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {entry.steps.map((step: any, idx: number) => (
                                <S.SubTicketStepChipWrapper
                                  key={idx}
                                  onMouseEnter={(e) => showSubTicketStepTooltip(e, step)}
                                  onMouseLeave={scheduleHideSubTicketStepTooltip}
                                >
                                  <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '3px', background: step.isCurrent ? '#0090D3' : '#e5e7eb', color: step.isCurrent ? '#fff' : '#333' }}>{step.stepName}</span>
                                </S.SubTicketStepChipWrapper>
                              ))}
                            </div>
                          )}
                          {entry.rejectionComment && (
                            <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>Motivo: {entry.rejectionComment}</div>
                          )}
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                            Enviado em: {new Date(entry.sentAt).toLocaleString('pt-BR')}
                            {entry.rejectedAt && ` | Rejeitado em: ${new Date(entry.rejectedAt).toLocaleString('pt-BR')}`}
                            {entry.completedAt && ` | Devolvido em: ${new Date(entry.completedAt).toLocaleString('pt-BR')}`}
                            {entry.completedAt && entry.sentAt && ` | Tempo: ${formatSubTicketDuration(Math.max(0, Math.floor((new Date(entry.completedAt).getTime() - new Date(entry.sentAt).getTime()) / 1000)))}`}
                          </div>
                        </S.SubTicketCard>
                      )
                    })}
                  </div>
                </S.Section>
                <S.SectionDivider />
              </>
            )}

            <S.Section>
              {shouldShowProductionTimeline && ticket.externalData?.currentServiceStageId ? (
                <CommentsOfOp serviceStageId={ticket.externalData.currentServiceStageId} />
              ) : (
                <CommentsOfOp ticketId={ticket.id} />
              )}
            </S.Section>
        </S.Content>
      </S.Container>

      {showSendToPipelineModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowSendToPipelineModal(false)}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '380px', maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>Enviar Para Outro Funil</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Selecione o funil destino para criar um subcard:</p>
            <select
              value={selectedTargetPipelineId || ''}
              onChange={(e) => handleSelectTargetPipeline(Number(e.target.value) || null)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', marginBottom: '16px' }}
            >
              <option value=''>Selecione...</option>
              {availablePipelines.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {selectedTargetPipelineId && (
              <>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Etapa do funil (opcional):</p>
                <select
                  value={selectedTargetStepId || ''}
                  onChange={(e) => setSelectedTargetStepId(Number(e.target.value) || null)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', marginBottom: '16px' }}
                >
                  <option value=''>Primeira etapa do funil (padrão)</option>
                  {availableTargetSteps.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSendToPipelineModal(false)} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
              <button onClick={handleConfirmSendToPipeline} disabled={!selectedTargetPipelineId || sendingToPipeline} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#0090D3', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, opacity: (!selectedTargetPipelineId || sendingToPipeline) ? 0.5 : 1 }}>{sendingToPipeline ? 'Enviando...' : 'Enviar'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {showFinishSubcardModal && (
        <ConfirmationModal
          isOpen={true}
          title='Finalizar subcard'
          message='Deseja finalizar este subcard e enviá-lo de volta para o card original?'
          actions={[
            {
              label: 'Cancelar',
              onClick: () => setShowFinishSubcardModal(false),
              variant: 'outline',
            },
            {
              label: finishingSubcard ? 'Finalizando...' : 'Finalizar',
              onClick: handleFinishSubcard,
              variant: 'primary',
            },
          ]}
        />
      )}
      {subTicketStepTooltip && ReactDOM.createPortal(
        <S.SubTicketStepTooltip
          $top={subTicketStepTooltip.top}
          $left={subTicketStepTooltip.left}
          $arrowLeft={subTicketStepTooltip.arrowLeft}
          $below={subTicketStepTooltip.below}
          onMouseEnter={cancelHideSubTicketStepTooltip}
          onMouseLeave={scheduleHideSubTicketStepTooltip}
          onClick={(e) => e.stopPropagation()}
        >
          <S.SubTicketTooltipHeader>{subTicketStepTooltip.step.stepName}</S.SubTicketTooltipHeader>
          <div style={{ fontSize: '10px', color: '#0284c7', marginBottom: '4px', fontWeight: 600 }}>
            Entrou em {subTicketStepTooltip.step.stepName}: {formatSubTicketTooltipDate(subTicketStepTooltip.step.enteredAt)}
          </div>
          <div style={{ fontSize: '10px', color: '#059669', marginBottom: '6px', fontWeight: 600 }}>
            Saiu de {subTicketStepTooltip.step.stepName}: {subTicketStepTooltip.step.exitedAt ? formatSubTicketTooltipDate(subTicketStepTooltip.step.exitedAt) : 'Em andamento'}
          </div>
          <S.SubTicketTooltipDivider />
          <S.SubTicketTooltipRow>
            <S.SubTicketTooltipLabel>Tempo na etapa:</S.SubTicketTooltipLabel>
            <S.SubTicketTooltipValue>{formatSubTicketDuration(subTicketStepTooltip.step.durationSeconds)}</S.SubTicketTooltipValue>
          </S.SubTicketTooltipRow>
          <S.SubTicketTooltipRow>
            <S.SubTicketTooltipLabel>Colocado por:</S.SubTicketTooltipLabel>
            <S.SubTicketTooltipValue>{subTicketStepTooltip.step.enteredBy || 'Sistema'}</S.SubTicketTooltipValue>
          </S.SubTicketTooltipRow>
          <S.SubTicketTooltipRow>
            <S.SubTicketTooltipLabel>Retirado por:</S.SubTicketTooltipLabel>
            <S.SubTicketTooltipValue>{subTicketStepTooltip.step.exitedAt ? (subTicketStepTooltip.step.exitedBy || 'Sistema') : 'Em andamento'}</S.SubTicketTooltipValue>
          </S.SubTicketTooltipRow>
          <S.SubTicketTooltipDivider />
          <S.SubTicketTooltipSectionTitle>Dados recebidos na etapa</S.SubTicketTooltipSectionTitle>
          {subTicketStepTooltip.step.receivedData && subTicketStepTooltip.step.receivedData.length > 0 ? (
            <S.SubTicketTooltipDataList>
              {subTicketStepTooltip.step.receivedData.map((data: any, dataIdx: number) => (
                <S.SubTicketTooltipDataItem key={dataIdx}>
                  <strong>{data.fieldName}:</strong> {formatReceivedFieldValue(data.value)}
                </S.SubTicketTooltipDataItem>
              ))}
            </S.SubTicketTooltipDataList>
          ) : (
            <S.SubTicketTooltipDataItem>Nenhum dado recebido</S.SubTicketTooltipDataItem>
          )}
        </S.SubTicketStepTooltip>,
        document.body
      )}
      {previewSubTicketId !== null && (
        <ViewOrEditTicket
          previewTicketId={previewSubTicketId}
          onClosePreview={() => setPreviewSubTicketId(null)}
        />
      )}
    </FormProvider>
  )
}

export default ViewOrEditTicket
